/**
 * The Matrix map: an SVG of the node graph, dragged to pan, pinched or
 * wheeled to zoom, tapped to open a node's sheet. Rendered as a string like
 * every panel and morphed in place; the camera lives in UiState so a refresh
 * never snaps the view back.
 */

import { MATRIX_NODES, MATRIX_BY_ID, ORIGIN, SPOKES, CROWN_RADIUS, levelCost, spokeAngle, spokeColor, type MatrixNode } from '../content/matrix';
import { FLEET_STAT_LABEL } from '../content/resources';
import { canReach, canRefund, isAllocated, matrixLevel, nextLevelCost } from '../sim/actions';
import type { GameState } from '../sim/state';
import { esc, fmtSigned, price } from './format';
import { btn, type MatrixCamera, type UiState, type View } from './view';

const RADIUS: Readonly<Record<MatrixNode['kind'], number>> = { core: 22, crown: 26, notable: 16, minor: 10 };
export const SPAN_MIN = 220;
export const SPAN_MAX = 1900;

function hexPoints(x: number, y: number, r: number): string {
  return Array.from({ length: 6 }, (_, k) => {
    const a = (Math.PI / 3) * k + Math.PI / 6;
    return `${(x + Math.cos(a) * r).toFixed(1)},${(y + Math.sin(a) * r).toFixed(1)}`;
  }).join(' ');
}

function diamondPoints(x: number, y: number, r: number): string {
  return `${x},${y - r} ${x + r},${y} ${x},${y + r} ${x - r},${y}`;
}

export function viewBox(c: MatrixCamera): string {
  return `${(c.x - c.span / 2).toFixed(1)} ${(c.y - c.span / 2).toFixed(1)} ${c.span.toFixed(1)} ${c.span.toFixed(1)}`;
}

function nodeSvg(state: GameState, node: MatrixNode, selected: boolean): string {
  const level = matrixLevel(state, node.id);
  const owned = isAllocated(state, node.id);
  const raise = canReach(state, node.id);
  const cost = node.id === ORIGIN ? null : nextLevelCost(state, node.id);
  const color = spokeColor(node.spoke);
  const r = RADIUS[node.kind];
  const maxed = owned && level >= node.maxLevel;
  // Fill deepens with each level; a maxed node is solid with a white rim.
  const fillOpacity = owned ? 0.35 + 0.65 * (level / Math.max(1, node.maxLevel)) : 1;
  const fill = owned ? color : '#0a0f1c';
  const stroke = maxed ? '#ffffff' : owned || raise ? color : '#253150';
  const width = maxed ? 2.5 : raise ? 3 : 2;
  const cls = raise && cost !== null && state.vs >= cost ? ' class="mx-afford"' : '';
  const paint = `fill="${fill}" fill-opacity="${fillOpacity.toFixed(2)}" stroke="${stroke}" stroke-width="${width}"${cls}`;
  const shape = node.kind === 'notable'
    ? `<polygon points="${hexPoints(node.x, node.y, r)}" ${paint}/>`
    : node.kind === 'crown'
      ? `<polygon points="${diamondPoints(node.x, node.y, r)}" ${paint}/>`
      : node.kind === 'core'
        ? `<circle cx="${node.x}" cy="${node.y}" r="${r}" fill="#d8f8ff" stroke="#4dd9f5" stroke-width="4"/>`
        : `<circle cx="${node.x}" cy="${node.y}" r="${r}" ${paint}/>`;
  // Multi-level nodes show their level once allocated.
  const label = owned && node.maxLevel > 1
    ? `<text x="${node.x}" y="${node.y}" fill="#ffffff" font-size="${node.kind === 'minor' ? 11 : 14}" font-weight="800" text-anchor="middle" dominant-baseline="central">${level}</text>`
    : '';
  const ring = selected ? `<circle cx="${node.x}" cy="${node.y}" r="${r + 7}" fill="none" stroke="#ffffff" stroke-width="2" stroke-dasharray="5 4"/>` : '';
  return `<g data-node="${node.id}">${shape}${label}${ring}<circle cx="${node.x}" cy="${node.y}" r="${Math.max(r, 24)}" fill="transparent"/></g>`;
}

export function renderMatrixMap(v: View): string {
  const { state, ui } = v;
  const selected = ui.sheet?.kind === 'node' ? ui.sheet.id : null;
  const edges: string[] = [];
  for (const node of MATRIX_NODES) {
    for (const other of node.neighbours) {
      if (other < node.id) continue;
      const o = MATRIX_BY_ID.get(other)!;
      const a = isAllocated(state, node.id);
      const b = isAllocated(state, other);
      const color = a && b ? spokeColor(node.spoke === null ? o.spoke : node.spoke) : a || b ? '#3a4a70' : '#1c2740';
      edges.push(`<line x1="${node.x}" y1="${node.y}" x2="${o.x}" y2="${o.y}" stroke="${color}" stroke-width="${a && b ? 5 : 2.5}" stroke-linecap="round"/>`);
    }
  }
  const labels = SPOKES.map((s, i) => {
    const a = spokeAngle(i);
    const r = CROWN_RADIUS + 70;
    return `<text x="${(Math.cos(a) * r).toFixed(0)}" y="${(Math.sin(a) * r).toFixed(0)}" fill="${s.color}" font-size="34" font-weight="800" text-anchor="middle" dominant-baseline="middle" letter-spacing="3">${s.label.toUpperCase()}</text>`;
  }).join('');
  const nodes = MATRIX_NODES.map((n) => nodeSvg(state, n, n.id === selected)).join('');
  return `<svg class="vm-mx-svg" viewBox="${viewBox(ui.camera)}" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">
    <g>${edges.join('')}</g><g>${labels}</g><g>${nodes}</g></svg>`;
}

/** A node's effects at a level: per-level amount × level (crowns are one-off). */
function effectText(node: MatrixNode, level: number): string {
  const times = node.kind === 'crown' ? Math.min(1, level) : level;
  return node.effects.map((e) => `<span style="color:${e.amount < 0 ? 'var(--red)' : 'var(--green)'}">${fmtSigned(e.amount * times)} ${FLEET_STAT_LABEL[e.stat]}</span>`).join(' · ');
}

export function renderNodeSheet(v: View, id: string): string {
  const { state } = v;
  const node = MATRIX_BY_ID.get(id)!;
  const level = matrixLevel(state, id);
  const raise = canReach(state, id);
  const cost = id === ORIGIN ? null : nextLevelCost(state, id);
  const color = spokeColor(node.spoke);
  const kind = { core: 'Origin', minor: 'Node', notable: 'Notable', crown: 'Crown' }[node.kind];
  let body = '';
  if (id === ORIGIN) {
    body = '<p class="vm-note">Every path begins here.</p>';
  } else {
    const now = level > 0 ? `<p class="vm-note" style="font-size:13px">Now: ${effectText(node, level)}</p>` : '';
    const next = cost !== null ? `<p class="vm-note" style="font-size:13px">${level > 0 ? 'Next level' : 'First level'}: ${effectText(node, level + 1)}</p>` : '<p class="vm-note"><b style="color:var(--green)">Maxed.</b></p>';
    const why = cost === null ? '' : !raise ? 'Allocate a neighbouring node first.' : state.vs < cost ? 'Not enough Void Shards.' : '';
    const up = cost === null ? '' : btn(`${level > 0 ? 'Upgrade' : 'Allocate'} · ${price('vs', cost)}`, 'allocateNode', { cls: 'magenta', disabled: !raise || state.vs < cost, data: { id } });
    const refundValue = level > 0 ? levelCost(node, level - 1) : null;
    const down = level > 0
      ? canRefund(state, id)
        ? btn(`Refund level · +${price('vs', refundValue!)}`, 'refundNode', { cls: 'ghost', data: { id } })
        : '<span class="vm-faint" style="font-size:11px">Other nodes depend on this one</span>'
      : '';
    body = `${now}${next}<div class="vm-btns" style="justify-content:flex-start">${up}${down}</div>${why ? `<p class="vm-note" style="margin-top:6px">${why}</p>` : ''}`;
  }
  return `<div class="vm-sheet-head"><h3 style="color:${color}">${esc(node.label)}</h3>${btn('✕', 'closeSheet', { cls: 'ghost' })}</div>
    <p class="vm-note">${kind}${node.spoke ? ` · ${SPOKES.find((sp) => sp.id === node.spoke)!.label}` : ''}${id !== ORIGIN ? ` · <b style="color:${level ? 'var(--green)' : 'var(--text-dim)'}">Level ${level}/${node.maxLevel}</b>` : ''}</p>
    ${node.kind === 'crown' ? `<p class="vm-note"><i>${esc(SPOKES.find((sp) => sp.id === node.spoke)!.crown.description)}</i></p>` : ''}
    ${body}`;
}

// ── Gestures ───────────────────────────────────────────────────────────────

/**
 * Attaches pan, pinch, wheel and tap handling to a host that contains the
 * map (delegated, so a morph that keeps or replaces the SVG is fine).
 */
export function bindMatrixGestures(host: HTMLElement, ui: UiState, onTapNode: (id: string) => void): void {
  const pointers = new Map<number, { x: number; y: number }>();
  let start: { x: number; y: number; cam: MatrixCamera; dist: number } | null = null;
  let moved = 0;
  const svgOf = (e: Event) => (e.target as Element).closest?.('.vm-mx-svg') as SVGSVGElement | null;
  const apply = (svg: SVGSVGElement) => svg.setAttribute('viewBox', viewBox(ui.camera));
  const spread = () => {
    const [a, b] = [...pointers.values()];
    return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
  };

  host.addEventListener('pointerdown', (e) => {
    const svg = svgOf(e);
    if (!svg) return;
    try { svg.setPointerCapture(e.pointerId); } catch { /* synthetic or already-released pointer */ }
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    start = { x: e.clientX, y: e.clientY, cam: { ...ui.camera }, dist: spread() };
    if (pointers.size === 1) moved = 0;
  });
  host.addEventListener('pointermove', (e) => {
    const svg = svgOf(e);
    if (!svg || !pointers.has(e.pointerId) || !start) return;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const scale = start.cam.span / svg.getBoundingClientRect().width;
    if (pointers.size >= 2 && start.dist > 0) {
      ui.camera.span = clampSpan(start.cam.span * (start.dist / Math.max(1, spread())));
      moved = 99;
    } else {
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      moved = Math.max(moved, Math.hypot(dx, dy));
      ui.camera.x = start.cam.x - dx * scale;
      ui.camera.y = start.cam.y - dy * scale;
    }
    apply(svg);
  });
  const end = (e: PointerEvent) => {
    if (!pointers.has(e.pointerId)) return;
    pointers.delete(e.pointerId);
    if (pointers.size === 0 && moved < 8) {
      const id = (e.target as Element).closest?.('[data-node]')?.getAttribute('data-node')
        ?? document.elementFromPoint(e.clientX, e.clientY)?.closest('[data-node]')?.getAttribute('data-node');
      if (id) onTapNode(id);
    }
    start = pointers.size ? { ...[...pointers.values()][0]!, cam: { ...ui.camera }, dist: spread() } : null;
  };
  host.addEventListener('pointerup', end);
  host.addEventListener('pointercancel', end);
  host.addEventListener('wheel', (e) => {
    const svg = svgOf(e);
    if (!svg) return;
    e.preventDefault();
    ui.camera.span = clampSpan(ui.camera.span * (e.deltaY > 0 ? 1.12 : 0.89));
    apply(svg);
  }, { passive: false });
}

export function clampSpan(span: number): number {
  return Math.max(SPAN_MIN, Math.min(SPAN_MAX, span));
}
